import { useEffect, useState } from 'react';
import { Megaphone, Plus, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { superFetch, formatDate } from '@/lib/super-admin-api';
import type { Announcement } from '@/lib/super-admin-types';

export function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', recipients: 'todos' });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    try {
      const res = await superFetch('/api/super-admin/announcements');
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(Array.isArray(data) ? data : data.data || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.message) {
      toast.error('Preencha título e mensagem');
      return;
    }
    setSending(true);
    try {
      const res = await superFetch('/api/super-admin/announcements', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title,
          message: form.message,
          recipients: form.recipients,
        }),
      });
      if (res.ok) {
        toast.success('Comunicado enviado com sucesso!');
        setShowCreate(false);
        setForm({ title: '', message: '', recipients: 'todos' });
        fetchAnnouncements();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao enviar comunicado');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setSending(false);
    }
  }

  const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>Comunicados</h1>
          <p className="text-base mt-1" style={{ color: '#94a3b8' }}>
            Envie mensagens para todos os estabelecimentos
          </p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-white transition-colors hover:opacity-90"
          style={{ background: '#10b981' }}
        >
          {showCreate ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showCreate ? 'Fechar' : 'Novo Comunicado'}
        </button>
      </div>

      {/* Create Form (Expandable Section) */}
      {showCreate && (
        <div className="rounded-xl border p-6 mb-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#f1f5f9' }}>Novo Comunicado</h3>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: '#94a3b8' }}>Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={inputStyle}
                placeholder="Ex: Manutenção programada"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: '#94a3b8' }}>Mensagem *</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full rounded-lg px-3 py-2.5 text-base resize-none outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={inputStyle}
                rows={4}
                placeholder="Escreva a mensagem do comunicado..."
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: '#94a3b8' }}>Destinatários</label>
              <select
                value={form.recipients}
                onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none"
                style={inputStyle}
              >
                <option value="todos">Todos os estabelecimentos</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="rounded-lg px-4 py-2.5 text-base font-medium transition-colors hover:opacity-80"
                style={{ color: '#94a3b8' }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={sending}
                className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ background: '#10b981' }}
              >
                <Send className="h-4 w-4" />
                {sending ? 'Enviando...' : 'Enviar Comunicado'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="animate-pulse rounded-xl p-8" style={{ background: '#1e293b' }}>
          <div className="h-32" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl" style={{ background: '#1e293b' }}>
          <Megaphone className="h-12 w-12 mb-3" style={{ color: '#475569' }} />
          <p className="text-base" style={{ color: '#94a3b8' }}>Nenhum comunicado enviado</p>
          <p className="text-sm mt-1" style={{ color: '#64748b' }}>Envie o primeiro comunicado para seus clientes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((ann) => (
            <div key={ann.id} className="rounded-xl border p-5" style={{ background: '#1e293b', borderColor: '#334155' }}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-base font-semibold" style={{ color: '#f1f5f9' }}>{ann.title}</h3>
                <span className="text-sm flex-shrink-0 ml-4" style={{ color: '#64748b' }}>
                  {formatDate(ann.sent_at || ann.created_at)}
                </span>
              </div>
              <p className="text-base mb-3" style={{ color: '#94a3b8' }}>{ann.message}</p>
              <div className="flex items-center gap-3 text-sm" style={{ color: '#64748b' }}>
                <span>Por: {ann.sender_name || 'Admin'}</span>
                <span>•</span>
                <span>
                  Para: {ann.recipients === 'todos' || ann.recipients === '"todos"'
                    ? 'Todos os estabelecimentos'
                    : 'Selecionados'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
